import { FormHelperText } from "@mui/material";
import { isEmpty } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";

const parseException = (err) => {
    return { "": err.message };
};

export const useForm = ({
    validate,
    onSubmit,
    data,
    parseErrors = parseException,
    mapDataToFields,
    mapFieldsToData,
} = {}) => {
    const [values, _setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const setValues = useCallback(
        (values, reset) => {
            !!reset && setErrors({});
            _setValues(values);
        },
        [_setValues, setErrors]
    );

    const handleSubmit = useCallback(
        async (fieldValues, initialData, submitPayload) => {
            const values = mapFieldsToData?.(fieldValues || {}) || fieldValues || {};

            setSubmitting(true);
            let errors = {};
            setErrors(errors);

            try {
                typeof validate === "function" && validate(values, errors);
                if (Object.keys(errors).length) {
                    console.warn("form has errors", errors);
                    setErrors(errors);
                    return;
                }

                if (onSubmit) {
                    await onSubmit(values, submitPayload || {}, { setErrors });
                }
            } catch (err) {
                console.error(err);
                if (typeof parseErrors === "function") {
                    errors = parseErrors(err);
                } else {
                    errors = { "": err.message };
                }
                setErrors(errors);
            }
            setSubmitting(false);
        },
        [mapFieldsToData, validate, onSubmit, parseErrors]
    );

    const handleChange = useCallback(
        (e) => {
            let isCheckbox = e.target?.type === "checkbox";
            let vals = {
                ...values,
                [e.target?.name]: isCheckbox ? e.target?.checked : e.target?.value,
            };
            _setValues(vals);
        },
        [_setValues, values]
    );

    //map data to fields
    useEffect(() => {
        setErrors({});
        const values = mapDataToFields?.(data || {}) || data || {};
        _setValues(values);
    }, [_setValues, setErrors, data, mapDataToFields]);

    return {
        values,
        setValues,
        handleChange,
        errors,
        setErrors,
        submitting,
        handleSubmit: (e, submitPayload) => {
            e && e.preventDefault();
            e && e.stopPropagation();
            handleSubmit(values, submitPayload || {});
        },
    };
};

// validations
export const validateMandatoryField = (value, field, errors) => {
    if (!value && value !== 0) {
        errors[field] = "Pole je povinné";
    }
};

export const validateNumberField = (value, field, errors) => {
    if (!isEmpty(value) && isNaN(value)) {
        errors[field] = "Neplatné číslo";
    }
};

export const validateDateField = (value, field, errors) => {
    if (isEmpty(value)) {
        return;
    }
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(value)) {
        errors[field] = "Neplatný datum";
    }
};

//TODO handle errors
export const hasErrors = (errors, field, parentPath, includeNested) => {
    if (!errors) {
        return false;
    }
    //TODO parentPath & includeNested
    return !!errors[field];
};

export const getErrors = (errors, field, parentPath, includeNested) => {
    if (!errors) {
        return null;
    }
    //TODO parentPath & includeNested
    return errors[field];
};

export const FormFieldErrors = ({ errors, field }) => {
    const _errors = useMemo(() => getErrors(errors, field), [errors, field]);
    if (!_errors) {
        return null;
    }

    if (Array.isArray(_errors)) {
        return (
            <>
                {errors.map((error, idx) => (
                    <React.Fragment key={`${idx}`}>
                        <FormHelperText error component="span">
                            {error}
                        </FormHelperText>
                    </React.Fragment>
                ))}
            </>
        );
    }

    //FIXME - normalize
    return (
        <FormHelperText error component="span">
            {_errors}
        </FormHelperText>
    );
};
